import { ReactNode, memo, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { Popconfirm } from "antd";
import { ICheckedForecast } from "store/features";
import {
  selectCheckedGlobalTypeWells,
  selectHasUnsavedChanges,
  selectOriginalCheckedGlobalTypeWells,
  useForecastStore
} from "stores/forecast";

interface UnsavedTypeWellChangesPopconfirmProps {
  children: ReactNode;
  open: boolean;
  placement?: "top" | "left" | "right" | "bottom";
  onConfirm: (event?: React.MouseEvent) => void;
  onCancel: () => void;
  isReset?: boolean;
  title?: string;
  checkedForecastsRef?: React.MutableRefObject<ICheckedForecast[]>;
}

export const UnsavedTypeWellChangesPopconfirm = memo(
  function UnsavedTypeWellChangesPopconfirm({
    children,
    open,
    placement,
    onConfirm,
    onCancel,
    isReset = true,
    title = "You have unsaved changes in your type well. Proceed without saving?"
  }: UnsavedTypeWellChangesPopconfirmProps) {
    const apolloClient = useApolloClient();

    const hasUnsavedChanges = useForecastStore(selectHasUnsavedChanges);
    const originalCheckedGlobalTypeWells = useForecastStore(
      selectOriginalCheckedGlobalTypeWells
    );
    const setHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
    const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
    const setOriginalCheckedGlobalTypeWells = useForecastStore(
      (s) => s.setOriginalCheckedGlobalTypeWells
    );
    const setSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
    const setCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
    const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);

    const handleConfirm = useCallback(
      async (event?: React.MouseEvent) => {
        setSelectedTypeWell(null);
        if (hasUnsavedChanges) {
          apolloClient.cache.evict({ fieldName: "folders" });
          apolloClient.cache.gc();

          setHasUnsavedChanges(false);
          if (originalCheckedGlobalTypeWells.length > 0 && isReset) {
            setCheckedGlobalTypeWells(originalCheckedGlobalTypeWells);
            setCheckedTypeWells(originalCheckedGlobalTypeWells);
            setCheckedTypeWellKeys(originalCheckedGlobalTypeWells.map((x) => x.id));
            setOriginalCheckedGlobalTypeWells([]);
          }
        }
        onConfirm(event);
      },
      [
        apolloClient,
        onConfirm,
        isReset,
        hasUnsavedChanges,
        originalCheckedGlobalTypeWells,
        setHasUnsavedChanges,
        setCheckedGlobalTypeWells,
        setOriginalCheckedGlobalTypeWells,
        setSelectedTypeWell,
        setCheckedTypeWells,
        setCheckedTypeWellKeys
      ]
    );

    const handleOpenChange = useCallback(
      (visible: boolean) => {
        if (!visible && open) {
          onCancel();
        }
      },
      [open, onCancel]
    );

    return (
      <Popconfirm
        open={open}
        placement={placement}
        title={title}
        okText="Confirm"
        cancelText="Cancel"
        onConfirm={handleConfirm}
        onCancel={onCancel}
        onOpenChange={handleOpenChange}>
        {children}
      </Popconfirm>
    );
  }
);

/**
 * Hook to manage unsaved changes state for navigation scenarios.
 * Returns helpers for checking and handling unsaved changes during navigation.
 */
export function useUnsavedTypeWellChangesGuard() {
  const hasUnsavedTypeWellChanges = useForecastStore(selectHasUnsavedChanges);

  /**
   * Check if navigation should be blocked and store pending navigation ID.
   * Returns true if navigation should proceed immediately, false if it should be blocked.
   */
  const shouldBlockNavigation = (
    pendingId: string | null,
    setPendingId: (id: string) => void
  ): boolean => {
    if (hasUnsavedTypeWellChanges) {
      setPendingId(pendingId);
      return true;
    }
    return false;
  };

  return {
    hasUnsavedTypeWellChanges,
    shouldBlockNavigation
  };
}
